import { onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from "vue";
import { getActivePinia, storeToRefs } from "pinia";
import { useIncidentStore, type IncidentSelectionCommand } from "@store/useIncidentStore";

export type { IncidentSelectionCommand };

interface UseIncidentSelectionOptions {
  index: number;
  labelAnchorId: MaybeRefOrGetter<string>;
  labelInstanceId: MaybeRefOrGetter<number>;
}

interface UseIncidentSelectionResult {
  selected: Ref<boolean>;
  toggleSelected: () => void;
}

function getIncidentStore() {
  const activePinia = getActivePinia();
  if (!activePinia) {
    throw new Error("[useIncidentSelection] Pinia is not active. Call app.use(createPinia()) before using incident selection helpers.");
  }

  return useIncidentStore(activePinia);
}

export function generateIncidentLabelId(service: string, region: string, id: string): string {
  return `${service}-${region}-${id}`.toLowerCase().replace(/\s/g, "");
}

export function applyIncidentSelectionCommand(command: IncidentSelectionCommand | null | undefined): void {
  const incidentStore = getIncidentStore();
  incidentStore.applySelectionCommand(command);
}

export function selectIncidentByAnchor(anchorId: string): void {
  const incidentStore = getIncidentStore();
  incidentStore.selectByAnchor(anchorId);
}

export function resetIncidentSelectionState(): void {
  const incidentStore = getIncidentStore();
  incidentStore.resetSelectionState();
}

export function useIncidentSelection(options: UseIncidentSelectionOptions): UseIncidentSelectionResult {
  const incidentStore = getIncidentStore();
  const { activeLabelInstanceId, selectionCommand } = storeToRefs(incidentStore);
  const selected = ref(options.index === 0);

  watch(
    selected,
    (value) => {
      const instanceId = toValue(options.labelInstanceId);
      if (value) {
        incidentStore.setActiveLabelInstance(instanceId);
        return;
      }

      if (activeLabelInstanceId.value === instanceId) {
        incidentStore.setActiveLabelInstance(null);
      }
    },
    { immediate: true },
  );

  watch(activeLabelInstanceId, (instanceId) => {
    if (!selected.value) {
      return;
    }

    if (instanceId !== toValue(options.labelInstanceId)) {
      selected.value = false;
    }
  });

  watch(selectionCommand, (command) => {
    if (!command) {
      return;
    }

    if (command.id === toValue(options.labelAnchorId)) {
      selected.value = true;
    }
  });

  onBeforeUnmount(() => {
    if (activeLabelInstanceId.value === toValue(options.labelInstanceId)) {
      incidentStore.resetSelectionState();
    }
  });

  function toggleSelected(): void {
    selected.value = !selected.value;
  }

  return {
    selected,
    toggleSelected,
  };
}
